import axios from 'axios'
import { useAuthStore } from '../store/authStore'

// Create axios instance
const apiClient = axios.create({
  baseURL: 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Handle responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 - unauthorized
    if (error.response?.status === 401) {
      useAuthStore.getState().logout()
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// ==================== AUTH ENDPOINTS ====================
export const authAPI = {
  login: (email, password) => apiClient.post('/api/v1/auth/login', { email, password }),
  logout: () => apiClient.post('/api/v1/auth/logout'),
  register: (data) => apiClient.post('/api/v1/auth/register', data),
  getProfile: () => apiClient.get('/api/v1/auth/profile'),
  updateProfile: (data) => apiClient.patch('/api/v1/auth/profile', data),
}

// ==================== TICKETS ENDPOINTS ====================
export const ticketsAPI = {
  getAll: (filters = {}) => apiClient.get('/api/v1/tickets', { params: filters }),
  getById: (id) => apiClient.get(`/api/v1/tickets/${id}`),
  create: (data) => apiClient.post('/api/v1/tickets', data),
  update: (id, data) => apiClient.patch(`/api/v1/tickets/${id}`, data),
  delete: (id) => apiClient.delete(`/api/v1/tickets/${id}`),
  addMessage: (ticketId, message) => apiClient.post(`/api/v1/tickets/${ticketId}/messages`, { text: message }),
  getMessages: (ticketId) => apiClient.get(`/api/v1/tickets/${ticketId}/messages`),
  assignTicket: (ticketId, agentId) => apiClient.patch(`/api/v1/tickets/${ticketId}/assign`, { agentId }),
  escalate: (ticketId, reason) => apiClient.post(`/api/v1/tickets/${ticketId}/escalate`, { reason }),
}

// ==================== AI/CHAT ENDPOINTS ====================
export const chatAPI = {
  sendMessage: (ticketId, message) =>
    apiClient.post(`/api/v1/chat/${ticketId}/message`, { text: message }),
  getResponse: (ticketId) =>
    apiClient.get(`/api/v1/chat/${ticketId}/response`),
  // For integration: TextNow SMS
  sendSMS: (phoneNumber, message) =>
    apiClient.post('/api/v1/sms/send', { phoneNumber, message }),
  // For integration: Claude AI
  getAIResponse: (ticketId, message) =>
    apiClient.post(`/api/v1/ai/response`, { ticketId, message }),
}

// ==================== KNOWLEDGE BASE ENDPOINTS ====================
export const knowledgeAPI = {
  getAllDocuments: (filters = {}) =>
    apiClient.get('/api/v1/knowledge/documents', { params: filters }),
  getDocument: (id) =>
    apiClient.get(`/api/v1/knowledge/documents/${id}`),
  createDocument: (data) =>
    apiClient.post('/api/v1/knowledge/documents', data),
  updateDocument: (id, data) =>
    apiClient.patch(`/api/v1/knowledge/documents/${id}`, data),
  deleteDocument: (id) =>
    apiClient.delete(`/api/v1/knowledge/documents/${id}`),
  uploadFile: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post('/api/v1/knowledge/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  search: (query) =>
    apiClient.get('/api/v1/knowledge/search', { params: { q: query } }),
  // For integration: Qdrant RAG
  retrieveContext: (query, limit = 5) =>
    apiClient.post('/api/v1/knowledge/rag/retrieve', { query, limit }),
}

// ==================== TRAINING ENDPOINTS ====================
export const trainingAPI = {
  getAllCourses: (filters = {}) =>
    apiClient.get('/api/v1/training/courses', { params: filters }),
  getCourse: (id) =>
    apiClient.get(`/api/v1/training/courses/${id}`),
  createCourse: (data) =>
    apiClient.post('/api/v1/training/courses', data),
  updateCourse: (id, data) =>
    apiClient.patch(`/api/v1/training/courses/${id}`, data),
  deleteCourse: (id) =>
    apiClient.delete(`/api/v1/training/courses/${id}`),
  enrollCourse: (courseId) =>
    apiClient.post(`/api/v1/training/courses/${courseId}/enroll`),
  updateProgress: (courseId, progress) =>
    apiClient.patch(`/api/v1/training/courses/${courseId}/progress`, { progress }),
  getLessons: (courseId) =>
    apiClient.get(`/api/v1/training/courses/${courseId}/lessons`),
  submitQuiz: (lessonId, answers) =>
    apiClient.post(`/api/v1/training/lessons/${lessonId}/quiz`, { answers }),
}

// ==================== ANALYTICS ENDPOINTS ====================
export const analyticsAPI = {
  getTicketStats: () =>
    apiClient.get('/api/v1/analytics/tickets'),
  getAIMetrics: () =>
    apiClient.get('/api/v1/analytics/ai'),
  getTrainingMetrics: () =>
    apiClient.get('/api/v1/analytics/training'),
  generateReport: (type, dateRange) =>
    apiClient.post('/api/v1/analytics/report', { type, dateRange }),
}

// ==================== SETTINGS ENDPOINTS ====================
export const settingsAPI = {
  getSettings: () =>
    apiClient.get('/api/v1/settings'),
  updateSettings: (data) =>
    apiClient.patch('/api/v1/settings', data),
  updateTeam: (data) =>
    apiClient.patch('/api/v1/settings/team', data),
  // For integration: SMS setup (TextNow)
  updateSMSConfig: (data) =>
    apiClient.patch('/api/v1/settings/sms', data),
  // For integration: Claude setup
  updateAIConfig: (data) =>
    apiClient.patch('/api/v1/settings/ai', data),
  // For integration: Qdrant setup
  updateRAGConfig: (data) =>
    apiClient.patch('/api/v1/settings/rag', data),
}

export default apiClient
